/**
 * 알림 표시 presenter (JCG-3601) — 클라이언트·서버 공용 순수 모듈.
 *
 * 종류(`type`)별로 payload 를 제목·설명 문구로 바꾼다. 새 종류를 추가하면 여기에 presenter 를 하나 더한다.
 * 링크는 저장된 `link` 를 그대로 쓰고, 상대 시각은 `formatRelativeTime` 로 만든다.
 * payload 는 JSONB 라 형태를 DB 가 보장하지 않는다 — 비어 있어도 "undefined님이" 가 나오지 않게 폴백을 둔다.
 */
import { COMMUNITY_ISSUE_STATUS_META, isNoteOnlyIssueEvent } from "@/lib/community-issues";
import type { AnyNotificationItem, NotificationType } from "@/types/notification";

export interface NotificationPresentation {
  /** 한 줄 제목(굵게) */
  title: string;
  /** 보조 설명(발췌 등). 없으면 빈 문자열 */
  description: string;
}

type Presenter<T extends NotificationType> = (
  item: Extract<AnyNotificationItem, { type: T }>,
) => NotificationPresentation;

function text(value: string | null | undefined, fallback = ""): string {
  return value?.trim() || fallback;
}

/** 설명 줄 공통 형식 — `"발췌" · 맥락(페이지·글 제목)`. 발췌가 없으면 맥락만, 맥락이 없으면 발췌만. */
function describe(excerpt: string | null | undefined, context: string | null | undefined): string {
  const quote = text(excerpt);
  const where = text(context);
  if (!quote) return where;
  return where ? `"${quote}" · ${where}` : `"${quote}"`;
}

const PRESENTERS: { [T in NotificationType]: Presenter<T> } = {
  reply: (item) => ({
    title: `${text(item.payload.replyAuthor, "누군가")}님이 내 댓글에 답글을 남겼어요`,
    description: describe(item.payload.replyExcerpt, item.payload.pageTitle),
  }),
  community_comment: (item) => ({
    title: `${text(item.payload.commenterName, "누군가")}님이 내 ${
      item.payload.board === "talk" ? "잡담 글" : "이슈"
    }에 댓글을 남겼어요`,
    description: describe(item.payload.commentExcerpt, item.payload.postTitle),
  }),
  issue_status: (item) => {
    const label = COMMUNITY_ISSUE_STATUS_META[item.payload.toStatus]?.label ?? String(item.payload.toStatus ?? "");
    return {
      title: isNoteOnlyIssueEvent(item.payload)
        ? `${text(item.payload.actorName, "운영자")}님이 내 이슈에 메모를 남겼어요`
        : `내 이슈가 '${label}' 상태로 바뀌었어요`,
      description: describe(item.payload.note, item.payload.issueTitle),
    };
  },
};

export function presentNotification(item: AnyNotificationItem): NotificationPresentation {
  // PRESENTERS 는 NotificationType 전체를 키로 강제하는 매핑이라 종류를 추가하고 presenter 를 빠뜨리면 컴파일 에러가 난다.
  // 알 수 없는 type 은 저장소 읽기(`isNotificationType`)에서 걸러지므로 여기까지 오지 않는다.
  const presenter = PRESENTERS[item.type] as Presenter<NotificationType>;
  return presenter(item);
}

/** 7일 이상 지난 알림의 날짜 표기 — 포맷터는 모듈에서 한 번만 만든다(행마다 생성하지 않음). */
const DATE_FORMAT = new Intl.DateTimeFormat("ko-KR", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * "방금 전"·"3분 전"·"2시간 전"·"어제"·"n일 전"·그 이후는 날짜. `now` 는 테스트용 주입.
 * 24시간 미만은 경과 시간, 그 이상은 **달력 날짜 차이**(보는 사람의 로컬 자정 기준)로 "어제"/"n일 전"을 정한다 —
 * 경과 시간만으로 정하면 이틀 전 밤 알림이 "어제" 로 보인다.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return "";
  const diffSeconds = Math.max(0, Math.round((now.getTime() - time) / 1000));
  if (diffSeconds < 60) return "방금 전";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;
  const diffDays = Math.max(
    1,
    Math.round((startOfLocalDay(now) - startOfLocalDay(new Date(time))) / 86_400_000),
  );
  if (diffDays === 1) return "어제";
  if (diffDays < 7) return `${diffDays}일 전`;
  return DATE_FORMAT.format(time);
}

/** 벨 뱃지 숫자 — 두 자리까지만 보여주고 넘으면 `99+`. */
export function formatUnreadBadge(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count > 99 ? "99+" : String(Math.floor(count));
}
